/** ShowingTime feed (v1 txn-expenses.js stFetch, verbatim contract):
 * GET /api/showings/feed?url=<iCal feed>&address=<deal address> → the
 * address-scoped showing events from the user's ShowingTime calendar sync.
 * The feed URL lives in org settings (SHOWINGTIME_FEED — connected from v1's
 * Showings tab; v2 reads it). Pure fetch + narrow; the panel renders states. */
import { backendFetch } from "../../lib/backend";

export interface StEvent {
  activity: string;
  start: string;
  agent: string;
  brokerage: string;
}

export type StFeedResult = { ok: true; events: StEvent[] } | { ok: false; detail: string };

const s = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

export async function fetchShowingFeed(
  token: string | null,
  feedUrl: string,
  address: string,
  opts?: { signal?: AbortSignal; onWake?: () => void },
): Promise<StFeedResult> {
  try {
    const q = new URLSearchParams({ url: feedUrl, address: address || "" });
    const j = await backendFetch<Record<string, unknown>>(token, "/api/showings/feed?" + q.toString(), {
      timeoutMs: 30000,
      ...(opts?.signal ? { signal: opts.signal } : {}),
      ...(opts?.onWake ? { onWake: opts.onWake } : {}),
    });
    const raw = Array.isArray(j?.["events"]) ? (j["events"] as unknown[]) : [];
    const events: StEvent[] = [];
    for (const e of raw) {
      if (e === null || typeof e !== "object") continue;
      const o = e as Record<string, unknown>;
      events.push({ activity: s(o["activity"]) || "Showing", start: s(o["start"]), agent: s(o["agent"]), brokerage: s(o["brokerage"]) });
    }
    return { ok: true, events };
  } catch (e) {
    const be = e as { detail?: string; message?: string };
    return { ok: false, detail: (be?.detail || be?.message || "Feed error").slice(0, 160) };
  }
}

/** v1 _stIcon — activity → emoji + tone class. */
export function stIcon(activity: string): { ic: string; cls: string } {
  const m: Record<string, { ic: string; cls: string }> = {
    "Cancelled": { ic: "✕", cls: "bad" },
    "Declined": { ic: "⊘", cls: "bad" },
    "Requested": { ic: "🕐", cls: "warn" },
    "Past Showing": { ic: "✓", cls: "dim" },
    "Confirmed": { ic: "📅", cls: "good" },
  };
  return m[activity] ?? { ic: "📅", cls: "dim" };
}

/** v1 _stFmt — ISO → "Wed, Jul 23, 2026 · 2:30 PM" (best-effort). */
export function stFmt(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}
